import { useEffect, useState } from "react";
import { Control, FieldValues, useController } from "react-hook-form";
import {
  Popover,
  Input,
  Portal,
  Box,
  Text,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Field,
  createListCollection,
  Select,
  SelectValueChangeDetails,
  Flex,
} from "@chakra-ui/react";
import { InputGroup } from "../ui/input-group";
import dayjs, { Dayjs } from "dayjs";

import { AppColors } from "@/styles/colors";
import { Calendar, MoveLeft, MoveRight, Trash } from "lucide-react";

import "dayjs/locale/es";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { v4 } from "uuid";
import { LightMode } from "../ui/color-mode";

// Configurar plugins de dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("es");

interface DatePickerProps {
  placeholder?: string;
  name: string;
  labelText?: string;
  control?: Control<FieldValues>;
  errors?: any;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  disablePortal?: boolean;
  height?: string;
  minHeight?: string;
  timezone?: string;
  required?: boolean;
  minYear?: number;
  maxYear?: number;
  clearable?: boolean;
  onChange?: (date: string | undefined) => void;
  onClear?: () => void;
  disabled?: boolean;
  popoverPlacement?: "left-start" | "right-start" | "bottom-start" | "bottom-end";
}

const DatePicker = ({
  placeholder = "Selecciona una fecha",
  name,
  labelText,
  control,
  errors,
  maxDate,
  disablePortal = true,
  required,
  height,
  minHeight,
  minYear,
  minDate,
  clearable = true,
  maxYear = dayjs().year(),
  timezone = "America/Argentina/Buenos_Aires",
  onChange,
  onClear,
  disabled,
  popoverPlacement = "left-start",
}: DatePickerProps) => {
  const { field } = useController({
    name,
    control,
  });

  // Configurar timezone por defecto
  const getDateInTimezone = (date?: string | Dayjs) => {
    if (!date) return dayjs().tz(timezone);
    return dayjs(date).tz(timezone);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(getDateInTimezone());

  const handleDateClick = (date: dayjs.Dayjs) => {
    // Crear la fecha en la timezone específica
    const dateInTimezone = date.tz(timezone);

    if (maxDate && dateInTimezone.isAfter(maxDate, "day")) return;

    // Guardar la fecha en formato YYYY-MM-DD manteniendo la timezone
    const formatted = dateInTimezone.format("YYYY-MM-DD");

    field.onChange(formatted);
    onChange?.(formatted);
    setCurrentMonth(dateInTimezone.startOf("month"));
    setIsOpen(false);
  };

  const daysInMonth = currentMonth.daysInMonth();
  // Obtener el día de la semana correctamente en la timezone
  const firstDayOfMonth = currentMonth.startOf("month").tz(timezone).day();
  const prevMonth = currentMonth.subtract(1, "month");
  const nextMonth = currentMonth.add(1, "month");
  const prevMonthDays = prevMonth.daysInMonth();

  const prevMonthDaysToShow = firstDayOfMonth;

  const [yearValue, setYearValue] = useState<string[]>([
    currentMonth.year().toString(),
  ]);

  const currentYear = getDateInTimezone().year();
  const min = minYear ?? currentYear - 100;
  const max = maxYear ?? currentYear;

  const years = createListCollection({
    items: Array.from({ length: max - min + 1 }).map((_, i) =>
      (max - i).toString(),
    ),
  });
  // FIX: Calcular los días del próximo mes que necesitamos mostrar
  const totalDaysDisplayed = prevMonthDaysToShow + daysInMonth;
  const nextMonthDaysToShow =
    totalDaysDisplayed % 7 === 0 ? 0 : 7 - (totalDaysDisplayed % 7);

  useEffect(() => {
    setYearValue([currentMonth.year().toString()]);
  }, [currentMonth]);

  const handlePreviousMonth = () => {
    const newMonth = currentMonth.subtract(1, "month");
    const newYear = newMonth.year();
    if (newYear >= min && newYear <= max) {
      setCurrentMonth(newMonth);
    }
  };

  const handleNextMonth = () => {
    const newMonth = currentMonth.add(1, "month");
    const newYear = newMonth.year();
    if (newYear >= min && newYear <= max) {
      setCurrentMonth(newMonth);
    }
  };

  const handleYear = (e: SelectValueChangeDetails<string>) => {
    const selectedYear = e.value?.[0];
    if (!selectedYear) return;

    const newYear = Number(selectedYear);
    setCurrentMonth(currentMonth.year(newYear));
    setYearValue([selectedYear]);
  };

  return (
    <LightMode>
      <Popover.Root
        open={isOpen}
        positioning={{ placement: popoverPlacement }}
        onOpenChange={(e) => setIsOpen(e.open)}
      >
        <Popover.Trigger asChild>
          <Field.Root invalid={!!errors}>
            {labelText && (
              <Field.Label>
                <Text fontWeight={600}>{labelText}</Text>
                {required && (
                  <Text fontWeight={600} color={AppColors.REQUIRED}>
                    *
                  </Text>
                )}
              </Field.Label>
            )}

            <InputGroup
              width="100%"
              flex="1"
              startElement={
                <Calendar
                  size="15px"
                  style={{ marginLeft: "12px", marginTop: "2px" }}
                />
              }
              endElement={
                field.value &&
                clearable && (
                  <Trash
                    data-testid="date-trash-button"
                    cursor={"pointer"}
                    size="15px"
                    style={{ marginRight: "10px" }}
                    onClick={(e) => {
                      field.onChange("");
                      setIsOpen(false);
                      onClear?.();
                      e.stopPropagation();
                    }}
                  />
                )
              }
              _placeholder={{
                color: AppColors.LIGHT_GRAY_TEXT,
              }}
            >
              <Input
                readOnly
                value={
                  field.value && dayjs(field.value).isValid()
                    ? getDateInTimezone(field.value).format("DD/MM/YYYY")
                    : ""
                }
                placeholder={placeholder}
                onClick={() => setIsOpen(!isOpen)}
                cursor="pointer"
                height={height}
                minHeight={minHeight}
                data-testid="date-picker-input"
                disabled={disabled}
                _disabled={{
                  cursor: "not-allowed",
                }}
              />
            </InputGroup>
            <Field.ErrorText>{errors?.message}</Field.ErrorText>
          </Field.Root>
        </Popover.Trigger>
        <Portal disabled={disablePortal}>
          <Popover.Positioner>
            <LightMode>
              <Popover.Content>
                <Popover.Arrow />
                <Box p={4}>
                  {/* Navegación entre meses */}
                  <HStack justify="space-between" mb={2}>
                    <IconButton
                      bgColor="transparent"
                      _hover={{
                        bgColor: "gray.200",
                      }}
                      data-testid="date-move-left-button"
                      type="button"
                      tabIndex={-1}
                      onClick={handlePreviousMonth}
                      disabled={
                        minDate &&
                        prevMonth.endOf("month").isBefore(minDate, "day")
                      }
                    >
                      <MoveLeft color="black" />
                    </IconButton>

                    <HStack align="center">
                      <Text fontWeight="medium">
                        {currentMonth
                          .format("MMMM")
                          .replace(/^./, (match) => match.toUpperCase())}
                      </Text>

                      <Select.Root
                        width="100px"
                        collection={years}
                        size="sm"
                        value={yearValue}
                        data-testid="date-picker-year-select"
                        onValueChange={(e) => handleYear(e)}
                      >
                        <Select.HiddenSelect />
                        <Select.Control>
                          <Select.Trigger>
                            <Select.ValueText paddingLeft="8px" />
                          </Select.Trigger>
                          <Select.IndicatorGroup paddingRight="8px">
                            <Select.Indicator />
                          </Select.IndicatorGroup>
                        </Select.Control>

                        <Select.Positioner>
                          <LightMode>
                            <Select.Content maxHeight="200px" overflowY="auto">
                              {years.items.map((year) => (
                                <Select.Item
                                  paddingLeft="8px"
                                  item={year}
                                  key={year}
                                >
                                  {year}
                                  <Select.ItemIndicator />
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </LightMode>
                        </Select.Positioner>
                      </Select.Root>
                    </HStack>
                    <IconButton
                      bgColor="transparent"
                      _hover={{
                        bgColor: "gray.200",
                      }}
                      data-testid="date-move-right-button"
                      type="button"
                      onClick={handleNextMonth}
                    >
                      <MoveRight color="black" />
                    </IconButton>
                  </HStack>

                  <Grid templateColumns="repeat(7, 1fr)" gap={1}>
                    {Array.from({ length: 7 }).map((_, i) => {
                      const dayName = dayjs().day(i).format("ddd");
                      return (
                        <GridItem key={v4()}>
                          <Text
                            textAlign="center"
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.600"
                          >
                            {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
                          </Text>
                        </GridItem>
                      );
                    })}
                  </Grid>

                  {/* Calendario */}
                  <Grid templateColumns="repeat(7, 1fr)" gap={1} mt={3}>
                    {/* Días del mes anterior */}
                    {Array.from({ length: prevMonthDaysToShow }).map((_, i) => {
                      const date = prevMonth
                        .date(prevMonthDays - prevMonthDaysToShow + i + 1)
                        .tz(timezone);
                      return (
                        <GridItem key={v4()}>
                          <Flex
                            p={2}
                            color="gray.400"
                            bg="gray.200"
                            borderRadius="md"
                            onClick={() => handleDateClick(date)}
                            justify={"center"}
                            align="center"
                          >
                            {date.date()}
                          </Flex>
                        </GridItem>
                      );
                    })}

                    {/* Días del mes actual */}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const date = currentMonth.date(i + 1).tz(timezone);
                      const isDisabled =
                        (maxDate && date.isAfter(maxDate, "day")) ||
                        (minDate && date.isBefore(minDate, "day"));
                      const isSelected =
                        field.value &&
                        dayjs(field.value).isValid() &&
                        getDateInTimezone(field.value).isSame(date, "day");

                      const disabledBGColor = isDisabled
                        ? "gray.200"
                        : "gray.100";

                      const disabledLabelColor = isDisabled
                        ? "gray.400"
                        : "black";
                      return (
                        <GridItem key={v4()}>
                          <Flex
                            p={2}
                            bg={
                              isSelected
                                ? AppColors.GREEN_PRIMARY
                                : disabledBGColor
                            }
                            color={isSelected ? "white" : disabledLabelColor}
                            borderRadius="md"
                            cursor={isDisabled ? "not-allowed" : "pointer"}
                            onClick={(e) => {
                              if (isDisabled) {
                                e.preventDefault();
                                e.stopPropagation();
                                return;
                              }
                              handleDateClick(date);
                            }}
                            justify={"center"}
                            align="center"
                          >
                            {i + 1}
                          </Flex>
                        </GridItem>
                      );
                    })}

                    {/* Días del mes siguiente */}
                    {Array.from({ length: nextMonthDaysToShow }).map((_, i) => {
                      const date = nextMonth.date(i + 1).tz(timezone);
                      const isDisabled =
                        maxDate && date.isAfter(maxDate, "day");

                      return (
                        <GridItem key={v4()}>
                          <Flex
                            p={2}
                            color="gray.400"
                            bg="gray.200"
                            borderRadius="md"
                            cursor={isDisabled ? "not-allowed" : "pointer"}
                            onClick={() => {
                              if (isDisabled) return;
                              handleDateClick(date);
                            }}
                            justify={"center"}
                            align="center"
                          >
                            {date.date()}
                          </Flex>
                        </GridItem>
                      );
                    })}
                  </Grid>
                </Box>
              </Popover.Content>
            </LightMode>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    </LightMode>
  );
};

export default DatePicker;
